import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText } from "lucide-react";

const SOURCE_LABEL = {
  purchase: "Fresh Purchase",
  fresh_unit: "Fresh Unit",
  damaged_set: "Damaged Set",
  refurbished_set: "Refurbished Set",
  technician_return: "Tech Return",
  adjustment: "Adjustment",
};

export default function SparesMRNList() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ source_type: "", part_code: "", start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/spares/mrn", { params });
      setRows(data || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPdf = (mrn) => downloadBlob({
    url: `/spares/mrn/${encodeURIComponent(mrn)}/pdf`,
    filename: `${mrn}.pdf`,
  });
  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5" data-testid="mrn-list-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Material Receipt Notes (MRN)</h1>
        <p className="text-sm text-slate-500 mt-1">Every stock increase flows through an MRN. Each MRN is printable.</p>
      </div>

      <Card><CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div><Label className="text-xs uppercase">From</Label><Input type="date" value={filters.start} onChange={(e) => upd("start", e.target.value)} data-testid="mrn-filter-start" /></div>
          <div><Label className="text-xs uppercase">To</Label><Input type="date" value={filters.end} onChange={(e) => upd("end", e.target.value)} data-testid="mrn-filter-end" /></div>
          <div><Label className="text-xs uppercase">Source</Label>
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                    value={filters.source_type} onChange={(e) => upd("source_type", e.target.value)} data-testid="mrn-filter-source">
              <option value="">All</option>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><Label className="text-xs uppercase">Part Code</Label><Input value={filters.part_code} onChange={(e) => upd("part_code", e.target.value)} data-testid="mrn-filter-part" /></div>
          <div className="flex items-end"><Button onClick={load} className="bg-slate-900 text-white w-full" data-testid="mrn-apply">Apply</Button></div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <Th>MRN #</Th><Th>Date</Th><Th>Source</Th>
                <Th>Part</Th><Th>Brand</Th>
                <Th align="right">Qty</Th><Th>Location</Th><Th>Condition</Th>
                <Th>Linked Complaint</Th><Th>Received By</Th><Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-slate-400">No MRNs.</td></tr>}
              {rows.map((m) => (
                <tr key={m.id} data-testid={`mrn-row-${m.mrn_number}`}>
                  <td className="font-mono">{m.mrn_number}</td>
                  <td className="text-xs">{m.mrn_date}</td>
                  <td>{SOURCE_LABEL[m.source_type] || m.source_type}</td>
                  <td>{m.part_name}{m.part_code && <span className="font-mono text-xs text-slate-500 ml-1">({m.part_code})</span>}</td>
                  <td>{m.brand || "—"}</td>
                  <td className="text-right font-mono">{m.quantity}</td>
                  <td>{m.location}</td>
                  <td>{m.condition}</td>
                  <td className="font-mono text-xs">{m.complaint_number || "—"}</td>
                  <td>{m.received_by}</td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(m.mrn_number)} data-testid={`mrn-pdf-${m.mrn_number}`}>
                      <FileText size={14} className="mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
